import React from 'react';

import { FileRejection } from 'react-dropzone';

import { noop } from '@adsk/alloy-react-helpers';

import { AttachmentsProviderProps } from './AttachmentsProvider';
import { DownloadMethod, UploadMethod } from '../consts';
import { Attachment, AttachmentDraft } from '../types';

export type TAttachmentsContext = {
  /**
   * The total number of attachments across all pages.
   * Used for calculating max attachment limits in pagination scenarios.
   * If not provided, falls back to the local attachments array length.
   */
  totalAttachments?: number;
  attachments: Attachment[];
  drafts: AttachmentDraft[];
  uploadIndicationIds: string[];
  addAttachments: ({
    files,
    method,
  }: {
    files: File[];
    method: UploadMethod;
  }) => void;
  deleteAttachment: (attachmentId: string) => void;
  hideAttachmentUploadIndication: (attachmentId: string) => void;
  deleteDraft: (attachmentId: string) => void;
  discardUnlinkedDrafts: () => void;
  downloadAttachment: ({
    attachmentId,
    method,
  }: {
    attachmentId: string;
    method: DownloadMethod;
  }) => void;
  openPDFAttachmentInNewTab: ({
    attachmentId,
  }: {
    attachmentId: string;
  }) => void;
  retryDraftUpload: (draftId: string) => void;
  contextualTrack: (eventName: string, data: Record<string, unknown>) => void;
  isUploading: boolean;
  handleRejectedAttachments: ({ files }: { files: FileRejection[] }) => void;
  openProjectFilesModal?: () => void;
} & Pick<
  AttachmentsProviderProps,
  | 'loading'
  | 'showLoadMore'
  | 'onLoadMore'
  | 'errorLoadingAttachments'
  | 'onAttachmentClick'
  | 'onRetryLoadingAttachments'
  | 'readOnly'
  | 'allowDelete'
  | 'maxNumberOfAttachments'
  | 'enableUploadOnPaste'
>;

export const AttachmentsContext = React.createContext<TAttachmentsContext>({
  totalAttachments: undefined,
  attachments: [],
  drafts: [],
  uploadIndicationIds: [],
  isUploading: false,
  allowDelete: true,
  readOnly: false,
  enableUploadOnPaste: true,
  deleteAttachment: noop,
  hideAttachmentUploadIndication: noop,
  deleteDraft: noop,
  addAttachments: noop,
  discardUnlinkedDrafts: noop,
  downloadAttachment: noop,
  openPDFAttachmentInNewTab: noop,
  onLoadMore: noop,
  retryDraftUpload: noop,
  contextualTrack: noop,
  handleRejectedAttachments: noop,
  onAttachmentClick: noop,
});

export const useAttachmentsContext = () => React.useContext(AttachmentsContext);

type PublicUseAttachmentsHookReturnValue = Pick<
  TAttachmentsContext,
  | 'drafts'
  | 'attachments'
  | 'uploadIndicationIds'
  | 'hideAttachmentUploadIndication'
  | 'addAttachments'
  | 'deleteAttachment'
  | 'discardUnlinkedDrafts'
  | 'retryDraftUpload'
  | 'isUploading'
  | 'errorLoadingAttachments'
  | 'loading'
  | 'downloadAttachment'
  | 'openPDFAttachmentInNewTab'
>;

// Public
export const useAttachments = (): PublicUseAttachmentsHookReturnValue => {
  const {
    drafts,
    attachments,
    uploadIndicationIds,
    hideAttachmentUploadIndication,
    addAttachments,
    deleteAttachment,
    discardUnlinkedDrafts,
    retryDraftUpload,
    isUploading,
    errorLoadingAttachments,
    loading,
    downloadAttachment,
    openPDFAttachmentInNewTab,
  } = useAttachmentsContext();

  return {
    drafts,
    attachments,
    addAttachments,
    deleteAttachment,
    discardUnlinkedDrafts,
    retryDraftUpload,
    isUploading,
    errorLoadingAttachments,
    loading,
    downloadAttachment,
    uploadIndicationIds,
    hideAttachmentUploadIndication,
    openPDFAttachmentInNewTab,
  };
};
